import React, { useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { CreateComisionistRequest } from 'types/api/responses';
import { getRequiredDocumentsByType } from '../utils/comisionist.validation';
import { uploadCommissionerDocument, deleteCommissionerDocument } from 'api/karthus.client';
import { Environment } from 'utils/environment';
import { isSuccessResult } from 'utils/guards';

type Document = {
  id?: string;
  type: string;
  fileName: string;
  file?: File;
  uploadedAt?: string;
  path?: string;
};

type Props = {
  formData: CreateComisionistRequest;
  documents: Document[];
  onDocumentsChange: (docs: Document[]) => void;
  onStatusChange?: (primaryStatus: string, secondaryStatus: string) => void;
};

export function ComisionistDocumentsForm({
  formData,
  documents,
  onDocumentsChange,
  onStatusChange,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [openCamera, setOpenCamera] = React.useState(false);
  const [selectedDocType, setSelectedDocType] = React.useState<string>('');
  const [cameraPreview, setCameraPreview] = React.useState<string>('');
  const [identityUploadType, setIdentityUploadType] = React.useState<'dui' | 'nit'>('dui');

  const requiredDocs = getRequiredDocumentsByType(formData.type);

  const uploadedDocs = requiredDocs.filter((doc) => documents.some((d) => d.type === doc));

  const missingDocs = requiredDocs.filter((doc) => !documents.some((d) => d.type === doc));

  // Subir: habilitado en New, Active o (Inactive con documentos faltantes)
  const canUploadDocuments =
    (formData as any).status === 'New' ||
    (formData as any).status === 'Active' ||
    !(formData as any).status ||
    ((formData as any).status === 'Inactive' && missingDocs.length > 0);

  // Eliminar: siempre habilitado para New, Active e Inactive (aunque los docs estén completos)
  const canDeleteDocuments =
    (formData as any).status === 'New' ||
    (formData as any).status === 'Active' ||
    (formData as any).status === 'Inactive' ||
    !(formData as any).status;

  const [isUploading, setIsUploading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const getApiDocType = (uiType: string) => {
    if (uiType === 'DUI/NIT') return identityUploadType;
    if (uiType === 'NRC') return 'nrc';
    if (uiType === 'Ficha Firmada') return 'signedFile';
    return 'unknown';
  };

  const handleFileSelect = (docType: string) => {
    if (!formData.clientAccount) {
      setSnackbar({
        open: true,
        message: 'Debe crear o guardar el comisionista primero antes de subir documentos',
        severity: 'error',
      });
      return;
    }
    setSelectedDocType(docType);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !formData.clientAccount) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Solo se permiten archivos PDF, JPG o PNG',
        severity: 'error',
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'El archivo no puede superar 5MB',
        severity: 'error',
      });
      return;
    }

    const apiDocType = getApiDocType(selectedDocType);
    setIsUploading(true);
    const res = await uploadCommissionerDocument(
      formData.clientAccount as string,
      apiDocType,
      file,
    );
    setIsUploading(false);

    if (isSuccessResult(res.result)) {
      const data = res.result as any;
      const responseData = data.data || {};
      const newPath =
        responseData[apiDocType + 'Doc'] ||
        responseData.identityDoc ||
        responseData.nrcDoc ||
        responseData.signedFile ||
        '';

      // Usar el path correcto según docType
      const docPathMap: Record<string, string> = {
        dui: responseData.identityDoc || '',
        nit: responseData.identityDoc || '',
        nrc: responseData.nrcDoc || '',
        signedFile: responseData.signedFile || '',
      };
      const resolvedPath = docPathMap[apiDocType] || '';

      const newDoc: Document = {
        id: apiDocType,
        type: selectedDocType,
        fileName: file.name,
        file: file,
        uploadedAt: new Date().toLocaleDateString('es-ES'),
        path: resolvedPath,
      };
      const filteredDocs = documents.filter((d) => d.type !== selectedDocType);
      onDocumentsChange([...filteredDocs, newDoc]);

      // Actualizar estados en el header desde la respuesta del API
      if (onStatusChange && responseData.primaryStatus && responseData.secondaryStatus) {
        onStatusChange(responseData.primaryStatus, responseData.secondaryStatus);
      }

      setSnackbar({
        open: true,
        message: `${selectedDocType} cargado exitosamente`,
        severity: 'success',
      });
    } else {
      const errors = res.result as Error[];
      setSnackbar({
        open: true,
        message: errors?.[0]?.message || `Error al subir ${selectedDocType}`,
        severity: 'error',
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const stopCameraStream = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleCloseCamera = () => {
    stopCameraStream();
    setOpenCamera(false);
  };

  React.useEffect(() => {
    if (openCamera) {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setSnackbar({
          open: true,
          message: 'El navegador no soporta cámara o requiere HTTPS',
          severity: 'error',
        });
        setOpenCamera(false);
        return;
      }

      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'environment' } })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          } else {
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.srcObject = stream;
              } else {
                stream.getTracks().forEach((track) => track.stop());
              }
            }, 500);
          }
        })
        .catch((err) => {
          console.error('Camera access error:', err);
          setSnackbar({
            open: true,
            message: 'No se pudo acceder a la cámara. Revisa los permisos.',
            severity: 'error',
          });
          setOpenCamera(false);
        });
    }
    return () => stopCameraStream();
  }, [openCamera]);

  const handleCameraClick = (docType: string) => {
    if (!formData.clientAccount) {
      setSnackbar({
        open: true,
        message: 'Debe crear o guardar el comisionista primero',
        severity: 'error',
      });
      return;
    }
    setSelectedDocType(docType);
    setOpenCamera(true);
  };

  const handleDeleteDocument = async (docId: string | undefined, docTypeUI: string) => {
    if (!docId || !formData.clientAccount) return;

    const apiDocType = getApiDocType(docTypeUI);

    setIsUploading(true);
    const res = await deleteCommissionerDocument(formData.clientAccount as string, apiDocType);
    setIsUploading(false);

    if (isSuccessResult(res.result)) {
      const data = res.result as any;
      const responseData = data.data || {};

      const newDocsArray = documents.filter((d) => d.id !== docId);
      onDocumentsChange(newDocsArray);

      // Actualizar estados en el header desde la respuesta del API
      if (onStatusChange && responseData.primaryStatus && responseData.secondaryStatus) {
        onStatusChange(responseData.primaryStatus, responseData.secondaryStatus);
      }

      setSnackbar({ open: true, message: `${docTypeUI} eliminado`, severity: 'success' });
    } else {
      const errors = res.result as Error[];
      setSnackbar({
        open: true,
        message: errors?.[0]?.message || `Error al eliminar ${docTypeUI}`,
        severity: 'error',
      });
    }
  };

  const handleViewDocument = (path?: string) => {
    if (!path) return;
    const url = path.startsWith('http') ? path : `${Environment.baseUrl}${path}`;
    window.open(url, '_blank');
  };

  const handleCameraCapture = () => {
    if (!videoRef.current || !canvasRef.current || !formData.clientAccount) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video stream
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async (blob) => {
          if (!blob) return;

          const fileName = `${selectedDocType.replace(/\s+/g, '_')}_${Date.now()}.jpg`;
          const file = new File([blob], fileName, { type: 'image/jpeg' });

          const apiDocType = getApiDocType(selectedDocType);

          handleCloseCamera();
          setIsUploading(true);

          const res = await uploadCommissionerDocument(
            formData.clientAccount as string,
            apiDocType,
            file,
          );
          setIsUploading(false);

          if (isSuccessResult(res.result)) {
            const data = res.result as any;
            const responseData = data.data || {};

            const docPathMap: Record<string, string> = {
              dui: responseData.identityDoc || '',
              nit: responseData.identityDoc || '',
              nrc: responseData.nrcDoc || '',
              signedFile: responseData.signedFile || '',
            };
            const resolvedPath = docPathMap[apiDocType] || '';

            const newDoc: Document = {
              id: apiDocType,
              type: selectedDocType,
              fileName: file.name,
              file: file,
              uploadedAt: new Date().toLocaleDateString('es-ES'),
              path: resolvedPath,
            };

            const filteredDocs = documents.filter((d) => d.type !== selectedDocType);
            onDocumentsChange([...filteredDocs, newDoc]);

            // Actualizar estados en el header desde la respuesta del API
            if (onStatusChange && responseData.primaryStatus && responseData.secondaryStatus) {
              onStatusChange(responseData.primaryStatus, responseData.secondaryStatus);
            }

            setSnackbar({
              open: true,
              message: `${selectedDocType} capturado exitosamente`,
              severity: 'success',
            });
          } else {
            const errors = res.result as Error[];
            setSnackbar({
              open: true,
              message: errors?.[0]?.message || `Error al subir captura de ${selectedDocType}`,
              severity: 'error',
            });
          }
        },
        'image/jpeg',
        0.85,
      );
    }
  };

  const getDocIcon = (docType: string) => {
    switch (docType) {
      case 'DUI/NIT':
        return <BadgeIcon fontSize="large" color="primary" />;
      case 'NRC':
        return <AccountBalanceIcon fontSize="large" color="primary" />;
      case 'Ficha Firmada':
        return <DescriptionIcon fontSize="large" color="primary" />;
      default:
        return <DescriptionIcon fontSize="large" color="primary" />;
    }
  };

  const isComplete = uploadedDocs.length === requiredDocs.length;

  return (
    <Box>
      {isComplete && (
        <Alert severity="success" style={{ marginBottom: '16px' }}>
          Todos los documentos requeridos han sido cargados
        </Alert>
      )}

      {missingDocs.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: '16px' }}>
          Documentos faltantes: {missingDocs.join(', ')}
        </Alert>
      )}

      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          title={`Documentos — Comisionista ${formData.type}`}
          subheader={`${uploadedDocs.length} / ${requiredDocs.length} documentos cargados`}
        />
        <CardContent>
          <Grid container spacing={2} alignItems="stretch">
            {requiredDocs.map((docType) => {
              const uploadedDoc = documents.find((d) => d.type === docType);
              const isMissing = !uploadedDoc;

              return (
                <Grid item xs={12} sm={6} md={4} key={docType} style={{ display: 'flex' }}>
                  <Paper
                    style={{
                      padding: '16px',
                      textAlign: 'center',
                      border: isMissing ? '2px dashed #c62828' : '2px solid #2e7d32',
                      backgroundColor: isMissing ? '#fff5f5' : '#f1f8e9',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <Box style={{ fontSize: '32px', marginBottom: '8px' }}>
                      {getDocIcon(docType)}
                    </Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      {docType}
                    </Typography>

                    {docType === 'DUI/NIT' && isMissing && canUploadDocuments && (
                      <Box mt={1} mb={1}>
                        <RadioGroup
                          row
                          value={identityUploadType}
                          onChange={(e) =>
                            setIdentityUploadType(e.target.value as 'dui' | 'nit')
                          }
                          style={{
                            justifyContent: 'center',
                            display: 'flex',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <FormControlLabel
                            style={{ minWidth: 'fit-content', padding: '0px' }}
                            value="dui"
                            control={
                              <Radio size="medium" style={{ color: 'rgb(26, 35, 126)' }} />
                            }
                            label={
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 600, color: 'rgb(26, 35, 126)' }}
                              >
                                DUI
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="nit"
                            style={{ minWidth: 'fit-content',  padding: '0px' }}
                            control={
                              <Radio size="medium" style={{ color: 'rgb(26, 35, 126)' }} />
                            }
                            label={
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 600, color: 'rgb(26, 35, 126)' }}
                              >
                                NIT
                              </Typography>
                            }
                          />
                        </RadioGroup>
                      </Box>
                    )}
                  </Box>

                    {uploadedDoc ? (
                      <>
                        <Typography
                          variant="caption"
                          style={{ color: '#2e7d32', display: 'block', marginTop: '4px' }}
                        >
                          {uploadedDoc.fileName}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: '#888', display: 'block', marginBottom: '8px' }}
                        >
                          {uploadedDoc.uploadedAt}
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="center"
                          flexWrap="wrap"
                          style={{ gap: '6px' }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(uploadedDoc.path)}
                            style={{ fontSize: '11px' }}
                          >
                            Ver
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={
                              isUploading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <DeleteIcon />
                              )
                            }
                            onClick={() => handleDeleteDocument(uploadedDoc.id,  docType)}
                            disabled={isUploading || !canDeleteDocuments}
                            style={{ fontSize: '11px' }}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#c62828',
                            display: 'block',
                            marginTop: '8px',
                            marginBottom: '8px',
                          }}
                        >
                          Requerido
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="center"
                          flexWrap="wrap"
                          style={{ gap: '6px' }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={
                              isUploading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CloudUploadIcon />
                              )
                            }
                            onClick={() => handleFileSelect(docType)}
                            disabled={isUploading || !canUploadDocuments}
                            style={{ fontSize: '11px' }}
                          >
                            Subir
                          </Button>
                          {/*  {docType !== 'NRC' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PhotoCameraIcon />}
                              onClick={() => handleCameraClick(docType)}
                              disabled={!canEditDocuments}
                              style={{ fontSize: '11px' }}
                            >
                              Escanear
                            </Button>
                          )} */}
                        </Box>
                      </>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Dialog para captura de cámara */}
      <Dialog open={openCamera} onClose={handleCloseCamera} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CameraAltIcon />
            <span>Escanear {selectedDocType}</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box
            style={{
              backgroundColor: '#000',
              height: '400px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '16px',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* Guide overlay */}
            <Box
              style={{
                position: 'absolute',
                border: '2px dashed rgba(255,255,255,0.6)',
                width: '80%',
                height: '80%',
                borderRadius: '8px',
                pointerEvents: 'none',
              }}
            />
          </Box>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCamera} disabled={isUploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleCameraCapture}
            variant="contained"
            color="primary"
            disabled={isUploading}
          >
            {isUploading ? <CircularProgress size={20} color="inherit" /> : 'Capturar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
